import { MagnifyingGlass, Star, X } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { type KeyboardEvent, type ReactNode, type Ref, type RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";
import type { NavDestination, NavHit } from "~/shell/navCatalog";
import { MAX_PINNED_NAV, useShellStore } from "~/shell/store";

/**
 * Shared building blocks of the layered navigation (2026-09-09): the search field, a
 * navigable row with its pin star, the results list and the arrow-key roving hook. The
 * product picker, the sidebar layers and the settings nav compose these — one look, one
 * behaviour, three surfaces.
 */

/** Marks an element as a keyboard-reachable row for `useNavRowKeys`. */
export const NAV_ROW_ATTR = "data-nav-row";

/**
 * ArrowUp/ArrowDown move focus across `[data-nav-row]` elements; the search input counts as
 * the row before the first. Home/End jump to the ends.
 */
export function useNavRowKeys(
  containerRef: RefObject<HTMLElement | null>,
): (event: KeyboardEvent) => void {
  return useCallback(
    (event: KeyboardEvent) => {
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        return;
      }
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const rows = Array.from(
        container.querySelectorAll<HTMLElement>(`input[type="search"], [${NAV_ROW_ATTR}]`),
      ).filter((element) => !element.hasAttribute("disabled"));
      if (rows.length === 0) {
        return;
      }
      const index = rows.indexOf(document.activeElement as HTMLElement);
      let next: number;
      if (event.key === "Home") {
        next = 0;
      } else if (event.key === "End") {
        next = rows.length - 1;
      } else if (event.key === "ArrowDown") {
        next = index < 0 ? 0 : Math.min(rows.length - 1, index + 1);
      } else {
        next = index <= 0 ? 0 : index - 1;
      }
      // Arrow keys inside the search field would move the caret instead.
      event.preventDefault();
      rows[next]?.focus();
    },
    [containerRef],
  );
}

export interface NavSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
  /** The sidebar variant is a touch shorter. */
  compact?: boolean;
}

export function NavSearchInput({
  value,
  onChange,
  placeholder,
  inputRef,
  compact,
}: NavSearchInputProps) {
  const { t } = useTranslation();
  const label = placeholder ?? t("Find an area…");

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-sm border border-input-border bg-bg px-2 text-text-tertiary",
        "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
        "focus-within:border-input-border-focus focus-within:text-text-secondary",
        compact ? "h-7" : "h-8",
      )}
    >
      <MagnifyingGlass size={16} aria-hidden className="shrink-0" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={label}
        aria-label={label}
        autoComplete="off"
        spellCheck={false}
        className="h-full min-w-0 flex-1 bg-transparent text-body text-text outline-none placeholder:text-placeholder [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={t("Clear")}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg hover:text-text"
        >
          <X size={14} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export interface PinToggleProps {
  id: string;
  size?: number;
  className?: string;
}

/**
 * The star that pins/unpins a destination. Stops propagation so it can sit beside a link
 * in a hover-revealed slot without triggering navigation.
 */
export function PinToggle({ id, size = 16, className }: PinToggleProps) {
  const { t } = useTranslation();
  const pinnedIds = useShellStore((state) => state.pinnedNavIds);
  const togglePinned = useShellStore((state) => state.togglePinned);
  const pinned = pinnedIds.includes(id);
  const disabled = !pinned && pinnedIds.length >= MAX_PINNED_NAV;
  const label = pinned ? t("Unpin") : disabled ? t("Pin limit reached") : t("Pin to favourites");

  return (
    <Tooltip content={label} placement="block-end">
      <button
        type="button"
        aria-pressed={pinned}
        aria-label={label}
        disabled={disabled}
        data-pinned={pinned ? "true" : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          togglePinned(id);
        }}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-sm",
          "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
          "disabled:cursor-default disabled:opacity-40",
          size > 16 ? "size-7" : "size-6",
          pinned
            ? "text-accent hover:bg-hover-bg"
            : "text-text-tertiary hover:bg-hover-bg hover:text-text",
          className,
        )}
      >
        <Star size={size} weight={pinned ? "fill" : "regular"} aria-hidden />
      </button>
    </Tooltip>
  );
}

export interface NavRowProps {
  destination: NavDestination;
  /** "Admin › Workspace" — shown under the name in search results. */
  crumb?: string;
  active: boolean;
  onNavigate?: () => void;
  showPin?: boolean;
}

/** One destination: icon, name, optional breadcrumb, pin star; keyboard-reachable. */
export function NavRow({ destination, crumb, active, onNavigate, showPin = true }: NavRowProps) {
  const { t } = useTranslation();
  const pinned = useShellStore((state) => state.pinnedNavIds.includes(destination.id));
  const Icon = destination.icon;

  return (
    <div
      className={cn(
        "group flex items-center rounded-sm",
        active ? "bg-sidebar-active-bg" : "hover:bg-hover-bg focus-within:bg-hover-bg",
      )}
    >
      <Link
        // The catalog stores plain paths; routes.tsx owns which paths exist.
        to={destination.to as never}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        data-nav-row=""
        className="flex min-h-9 min-w-0 flex-1 items-center gap-2.5 rounded-sm px-2 py-1 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-input-border-focus"
      >
        <Icon size={18} aria-hidden className="shrink-0 text-text-secondary" />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className={cn("truncate", active && "font-medium")}>{t(destination.title)}</span>
          {crumb ? <span className="truncate text-small text-text-tertiary">{crumb}</span> : null}
        </span>
      </Link>
      {showPin ? (
        <span
          data-pinned={pinned ? "true" : undefined}
          className="pe-1 opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100 data-[pinned=true]:opacity-100 motion-reduce:transition-none"
        >
          <PinToggle id={destination.id} />
        </span>
      ) : null}
    </div>
  );
}

export interface NavResultsProps {
  hits: NavHit[];
  query: string;
  activeId?: string;
  onNavigate?: () => void;
}

/** Search hits across every section, with breadcrumbs. */
export function NavResults({ hits, query, activeId, onNavigate }: NavResultsProps) {
  const { t } = useTranslation();

  if (hits.length === 0) {
    return (
      <p role="status" className="px-3 py-5 text-center text-body text-text-tertiary">
        {t("No areas match “{{query}}”", { query: query.trim() })}
      </p>
    );
  }

  return (
    <div role="list" aria-label={t("Search results")} className="flex flex-col gap-0.5 px-1">
      {hits.map((hit) => (
        <NavRow
          key={hit.destination.id}
          destination={hit.destination}
          crumb={hit.crumb}
          active={hit.destination.id === activeId}
          {...(onNavigate ? { onNavigate } : {})}
        />
      ))}
    </div>
  );
}

export function GroupHeading({ children }: { children: ReactNode }) {
  return (
    <p className="select-none px-3 pt-2.5 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
      {children}
    </p>
  );
}
